import {
  AudioLines,
  Clapperboard,
  Image as ImageIcon,
  Mic2,
  PenLine,
  PencilRuler,
  SlidersHorizontal,
  SlidersVertical,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import type { Release } from '@/types';

type CreditRow = { role: string; name: string };

const CREDIT_ROLE_META: Record<
  string,
  { label: string; pluralLabel?: string; Icon: LucideIcon }
> = {
  producer: { label: 'Producer', pluralLabel: 'Producers', Icon: SlidersVertical },
  // Master stays singular even with multiple names.
  master: { label: 'Master', Icon: AudioLines },
  artwork: { label: 'Artwork', Icon: ImageIcon },
  design: { label: 'Design', pluralLabel: 'Designs', Icon: PencilRuler },
  writer: { label: 'Writer', pluralLabel: 'Writers', Icon: PenLine },
  mixer: { label: 'Mixer', pluralLabel: 'Mixers', Icon: SlidersHorizontal },
  vocals: { label: 'Vocals', Icon: Mic2 },
  director: { label: 'Director', pluralLabel: 'Directors', Icon: Clapperboard },
  other: { label: 'Other', pluralLabel: 'Others', Icon: UserRound },
};

function normalizeCreditRole(raw: string): string {
  const r = String(raw || '')
    .trim()
    .toLowerCase();
  if (!r) return 'other';
  if (['master', 'mastering', 'mastered'].includes(r)) return 'master';
  if (['producer', 'produced', 'production'].includes(r)) return 'producer';
  if (['design', 'designer'].includes(r)) return 'design';
  if (['artwork', 'art', 'cover'].includes(r)) return 'artwork';
  if (['writer', 'written', 'songwriter', 'lyrics', 'lyric'].includes(r))
    return 'writer';
  if (['mixer', 'mixed', 'mix', 'mixing'].includes(r)) return 'mixer';
  if (['vocals', 'vocal', 'singer', 'singing'].includes(r)) return 'vocals';
  if (['director', 'directed', 'direction'].includes(r)) return 'director';
  if (r in CREDIT_ROLE_META) return r;
  return 'other';
}

function creditRoleLabel(role: string, count = 1): string {
  const id = normalizeCreditRole(role);
  const known = CREDIT_ROLE_META[id];
  const plural = count > 1;
  if (known) {
    if (plural && known.pluralLabel) return known.pluralLabel;
    return known.label;
  }
  if (!role) return plural ? 'Others' : 'Other';
  const base = role[0].toUpperCase() + role.slice(1);
  if (!plural) return base;
  return /s$/i.test(base) ? base : `${base}s`;
}

function creditRoleIcon(role: string): LucideIcon {
  return CREDIT_ROLE_META[normalizeCreditRole(role)]?.Icon ?? UserRound;
}

/**
 * Track-scoped lines from catalog prose (“Track 3 written by…” → name ends
 * with “(Track 3)”) or an explicit track label.
 * Hidden on desktop / album chrome; shown only in the mobile vaul player when
 * `trackPosition` matches.
 */
function isTrackSpecificCredit(row: CreditRow): boolean {
  const name = row.name.trim();
  const role = row.role.trim();
  if (/\(\s*track\s+\d+\s*\)/i.test(name)) return true;
  if (/^track\s+\d+\b/i.test(name)) return true;
  if (/^track\s+\d+\b/i.test(role)) return true;
  return false;
}

function trackNumberFromCredit(row: CreditRow): number | null {
  const name = row.name.trim();
  const role = row.role.trim();
  const paren = name.match(/\(\s*track\s+(\d+)\s*\)/i);
  if (paren) return Number(paren[1]);
  const nameLead = name.match(/^track\s+(\d+)\b/i);
  if (nameLead) return Number(nameLead[1]);
  const roleLead = role.match(/^track\s+(\d+)\b/i);
  if (roleLead) return Number(roleLead[1]);
  return null;
}

function stripTrackSuffix(name: string): string {
  return name.replace(/\s*\(\s*track\s+\d+\s*\)\s*$/i, '').trim();
}

/**
 * Split co-credited people joined with “&” into discrete names.
 * Keeps a trailing “(Track N)” suffix on each part when present.
 * Does not split on the word “and” (band names like “Tegan and Sara”).
 */
function splitCreditNames(name: string): string[] {
  const raw = name.trim();
  if (!raw) return [];
  const track = raw.match(/\s*(\(\s*track\s+\d+\s*\))\s*$/i);
  const base = track ? raw.slice(0, track.index).trim() : raw;
  const suffix = track ? ` ${track[1]}` : '';
  const parts = base
    .split(/\s*&\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  return parts.map((p) => `${p}${suffix}`);
}

/** Last whitespace-separated token — used for alphabetical credit ordering. */
function creditLastName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return parts[parts.length - 1]!;
}

function compareCreditRows(a: CreditRow, b: CreditRow): number {
  const roleCmp = creditRoleLabel(a.role).localeCompare(
    creditRoleLabel(b.role),
    undefined,
    { sensitivity: 'base' },
  );
  if (roleCmp !== 0) return roleCmp;
  const lastCmp = creditLastName(a.name).localeCompare(
    creditLastName(b.name),
    undefined,
    { sensitivity: 'base' },
  );
  if (lastCmp !== 0) return lastCmp;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

type CreditGroup = { role: string; names: string[] };

/** Same role → one label with each person on their own name line. */
function groupCreditRows(rows: CreditRow[]): CreditGroup[] {
  const groups: CreditGroup[] = [];
  for (const row of rows) {
    const roleId = normalizeCreditRole(row.role);
    const last = groups[groups.length - 1];
    if (last && normalizeCreditRole(last.role) === roleId) {
      last.names.push(row.name);
    } else {
      groups.push({ role: row.role, names: [row.name] });
    }
  }
  return groups;
}

function parseCreditRows(credits: Release['credits']): CreditRow[] | null {
  if (credits == null || credits === '') return null;
  if (Array.isArray(credits)) {
    const rows = credits
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const role = String(row.role || '').trim();
        const name = String(row.name || '').trim();
        if (!role && !name) return null;
        return { role: role || 'other', name };
      })
      .filter(Boolean) as CreditRow[];
    return rows.length ? rows : null;
  }
  if (typeof credits !== 'string') return null;
  const text = credits.trim();
  if (!text || /<\s*[a-z]/i.test(text)) return null;
  const rows = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^(.+?)\s*[—–\-:]\s*(.+)$/);
      if (!m) return { role: 'other', name: line };
      return { role: m[1].trim(), name: m[2].trim() };
    });
  return rows.length ? rows : null;
}

/** Release-wide rows always; track-scoped only when `trackPosition` matches. */
function selectCreditRows(
  credits: Release['credits'],
  trackPosition?: number | null,
): CreditRow[] | null {
  const rows = parseCreditRows(credits);
  if (!rows) return null;
  const out: CreditRow[] = [];
  for (const row of rows) {
    if (!row.name.trim()) continue;
    const base = isTrackSpecificCredit(row)
      ? (() => {
          if (trackPosition == null || !Number.isFinite(trackPosition)) {
            return null;
          }
          const n = trackNumberFromCredit(row);
          if (n !== trackPosition) return null;
          return {
            role: row.role,
            name: stripTrackSuffix(row.name) || row.name,
          };
        })()
      : row;
    if (!base) continue;
    for (const name of splitCreditNames(base.name)) {
      out.push({ role: base.role, name });
    }
  }
  if (!out.length) return null;
  out.sort(compareCreditRows);
  return out;
}

export function hasCredits(
  credits: Release['credits'],
  trackPosition?: number | null,
): boolean {
  return Boolean(selectCreditRows(credits, trackPosition));
}

export function CreditsList({
  credits,
  trackPosition,
}: {
  credits: Release['credits'];
  /** When set, include track-scoped credits for this 1-based track position. */
  trackPosition?: number | null;
}) {
  const rows = selectCreditRows(credits, trackPosition);
  if (rows) {
    const groups = groupCreditRows(rows);
    return (
      <ul className="credits-list">
        {groups.map((group, i) => {
          const Icon = creditRoleIcon(group.role);
          const multi = group.names.length > 1;
          const label = creditRoleLabel(group.role, group.names.length);
          return (
            <li
              key={`${group.role}-${group.names.join('|')}-${i}`}
              className={`credit-row${multi ? ' is-multi' : ''}`}
            >
              <span className="credit-role">
                <Icon size={12} strokeWidth={1.75} aria-hidden />
                {label}
              </span>
              <span className="credit-names">
                {group.names.map((name) => (
                  <span key={name} className="credit-name">
                    {name || '—'}
                  </span>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }
  // HTML / unstructured prose: only show when it does not look track-scoped.
  if (typeof credits === 'string' && credits.trim()) {
    const html = credits.trim();
    if (/\btrack\s+\d+\b/i.test(html)) return null;
    return /<\s*[a-z]/i.test(html) ? (
      <div
        className="credits-fallback"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    ) : (
      <div className="credits-fallback whitespace-pre-line">{html}</div>
    );
  }
  return null;
}
